import type { ImprensaContato } from './contatos';

/** Um contato junto do índice que ele ocupa em `data.contatos`. */
export interface ContatoIndexado {
  contato: ImprensaContato;
  /**
   * Índice na lista CRUA do bloco — é ele que o lápis do modo de edição manda
   * para o `ObjectListWidget`. Guardá-lo aqui é o que permite abrir o item
   * certo mesmo com a busca preenchida ou com linhas incompletas descartadas.
   */
  index: number;
}

/**
 * Caixa baixa, sem acento e sem espaço nas pontas.
 *
 * `NFD` separa a letra do diacrítico e o intervalo U+0300-U+036F apaga só o
 * diacrítico — é o que faz "para" achar `PR/PA` e "ceara" achar `PR/CE` sem
 * pedir que o visitante saiba onde vai o acento.
 */
export const normalizeTermo = (valor?: string): string =>
  (valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** Só os dígitos — usado no `tel:` e na busca por número. */
const somenteDigitos = (valor?: string): string =>
  (valor ?? '').replace(/\D/g, '');

/**
 * Texto onde a busca procura. Além dos campos crus, entra a sigla sem a barra
 * (`prac`) e o telefone só com dígitos: são as duas formas que a pessoa digita
 * sem pensar.
 */
const paraBusca = (contato: ImprensaContato): string =>
  [
    normalizeTermo(contato.unidade),
    normalizeTermo(contato.unidade).replace(/[^a-z0-9]/g, ''),
    normalizeTermo(contato.nome),
    normalizeTermo(contato.email),
    normalizeTermo(contato.telefone),
    somenteDigitos(contato.telefone),
  ].join(' ');

/**
 * Descarta o que o editor deixou pela metade — sem sigla não há o que exibir —
 * sem perder o índice original de cada linha.
 */
export const toContatosIndexados = (
  contatos?: ImprensaContato[],
): ContatoIndexado[] =>
  (contatos ?? [])
    .map((contato, index) => ({ contato, index }))
    .filter(({ contato }) => Boolean(contato?.unidade?.trim()));

/**
 * Um contato casa com o termo? Termo vazio casa com todos — o painel nunca
 * começa vazio.
 */
export const matchContato = (
  contato: ImprensaContato,
  termo: string,
): boolean => {
  const alvo = normalizeTermo(termo);
  return alvo ? paraBusca(contato).includes(alvo) : true;
};

/** Conveniência sobre `matchContato`, para quem não precisa dos índices. */
export const filtrarContatos = (
  contatos: ImprensaContato[],
  termo: string,
): ImprensaContato[] =>
  contatos.filter((contato) => matchContato(contato, termo));

/**
 * `href` do telefone. Quando há dois números (`(91) 3299-0212 / 0148`), o link
 * leva ao primeiro: o segundo é ramal e discá-lo sozinho não completa a
 * chamada. `+55` porque o número é sempre nacional com DDD.
 */
export const toTelHref = (telefone?: string): string | undefined => {
  const digitos = somenteDigitos((telefone ?? '').split('/')[0]);
  // 0800/0300/0500/0900 não são geográficos: só discam sem o código do país.
  if (/^0[3589]00\d{6,7}$/.test(digitos)) return `tel:${digitos}`;
  return digitos.length >= 10 ? `tel:+55${digitos}` : undefined;
};

/**
 * Estados da lista, sem repetição e em ordem alfabética, para o filtro por
 * estado. O nome por extenso é o estado; sem ele, a sigla da unidade.
 */
export const estadoDoContato = (contato: ImprensaContato): string =>
  contato.nome?.trim() || contato.unidade.trim();

export const estadosDosContatos = (contatos: ContatoIndexado[]): string[] =>
  Array.from(
    new Set(contatos.map(({ contato }) => estadoDoContato(contato))),
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

/**
 * UF da unidade, quando a sigla traz uma (`PR/DF` -> `DF`). PGR e PRRs não têm
 * UF na sigla e devolvem `''`.
 */
export const ufDoContato = (contato: ImprensaContato): string =>
  /\/\s*([A-Za-z]{2})\s*$/
    .exec(contato.unidade?.trim() ?? '')?.[1]
    ?.toUpperCase() ?? '';

/**
 * UFs de cada estado da lista — é o que deixa "DF" achar "Distrito Federal"
 * no filtro por estado, onde as opções são só os nomes por extenso.
 */
export const ufsPorEstado = (
  contatos: ContatoIndexado[],
): Map<string, string[]> => {
  const mapa = new Map<string, string[]>();
  contatos.forEach(({ contato }) => {
    const estado = estadoDoContato(contato);
    const uf = ufDoContato(contato);
    const ufs = mapa.get(estado) ?? [];
    if (uf && !ufs.includes(uf)) ufs.push(uf);
    mapa.set(estado, ufs);
  });
  return mapa;
};
